import React from 'react';
import {
  Show,
  TextField,
  DateField,
  BooleanField,
  ReferenceField,
  TabbedShowLayout,
  Tab,
  ReferenceManyField,
  Datagrid,
  EmailField,
  useRecordContext,
} from 'react-admin';
import { Card, CardContent, Typography, Chip, Grid, Box } from '@mui/material';

const ExperienceSection = () => {
  const record = useRecordContext();
  if (!record) {
    return null;
  }

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Driving Experience
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2">Canada Experience</Typography>
            <BooleanField source="hasCanadaExperience" />
            {record.canadaExperience && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                {record.canadaExperience}
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2">USA Experience</Typography>
            <BooleanField source="hasUsaExperience" />
            {record.usaExperience && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                {record.usaExperience}
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2">International Experience</Typography>
            <BooleanField source="hasInternationalExperience" />
            {record.internationalExperience && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                {record.internationalExperience}
              </Typography>
            )}
          </Grid>
          {record.experienceRadius && (
            <Grid item xs={12}>
              <Typography variant="subtitle2">Experience Radius</Typography>
              <Chip label={record.experienceRadius} color="primary" size="small" />
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

const SafetySection = () => {
  const record = useRecordContext();
  if (!record) {
    return null;
  }

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Safety Record
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2">Recent Accidents</Typography>
            <BooleanField source="hasRecentAccident" />
            {record.hasRecentAccident && record.recentAccidentDescription && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                {record.recentAccidentDescription}
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2">Recent Tickets</Typography>
            <BooleanField source="hasRecentTicket" />
            {record.hasRecentTicket && record.recentTicketDescription && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                {record.recentTicketDescription}
              </Typography>
            )}
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2">Drug/Alcohol Issues (3 years)</Typography>
            <BooleanField source="drugAlcohol3Year" />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

const DocumentsSection = () => {
  const record = useRecordContext();
  if (!record) {
    return null;
  }

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Documentation & Authorization
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2">Citizenship & Work Authorization</Typography>
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2">
                Canadian Citizen: <BooleanField source="canadianCitizen" />
              </Typography>
              <Typography variant="body2">
                Can Live/Work in Canada: <BooleanField source="canadaLiveAndWork" />
              </Typography>
              <Typography variant="body2">
                Can Enter USA: <BooleanField source="canEnterUsa" />
              </Typography>
              <Typography variant="body2">
                USA Entry Denied: <BooleanField source="usaDenied" />
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2">Travel Documents</Typography>
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2">
                Valid Passport: <BooleanField source="hasValidPassport" />
              </Typography>
              {record.passportCountryOrigin && (
                <Typography variant="body2">
                  Passport Country: {record.passportCountryOrigin}
                </Typography>
              )}
              {record.passportExpiry && (
                <Typography variant="body2">
                  Passport Expiry: <DateField source="passportExpiry" />
                </Typography>
              )}
              <Typography variant="body2">
                FAST Pass: <BooleanField source="hasFastPass" />
              </Typography>
              {record.fastPassNumber && (
                <Typography variant="body2">FAST Pass #: {record.fastPassNumber}</Typography>
              )}
              <Typography variant="body2">
                US DOT Number: <BooleanField source="hasUsDotNumber" />
              </Typography>
              {record.usDotNumber && (
                <Typography variant="body2">US DOT #: {record.usDotNumber}</Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export const DriverShow = () => (
  <Show>
    <TabbedShowLayout>
      <Tab label="Overview">
        <ReferenceField source="userId" reference="users" link="show">
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField source="userId" reference="users" link={false}>
          <EmailField source="email" />
        </ReferenceField>
        <TextField source="profileState" />
        <DateField source="createdAt" />
        <DateField source="updatedAt" />

        <ExperienceSection />
        <SafetySection />
        <DocumentsSection />
      </Tab>

      <Tab label="Licenses">
        <ReferenceManyField reference="driver-licenses" target="driverId">
          <Datagrid>
            <ReferenceField source="licenseTypeId" reference="license-types">
              <TextField source="name" />
            </ReferenceField>
            <TextField source="code" />
            <DateField source="expiry" />
            <TextField source="status" />
            <DateField source="createdAt" />
          </Datagrid>
        </ReferenceManyField>
      </Tab>

      <Tab label="Certifications">
        <ReferenceManyField reference="driver-certifications" target="driverId">
          <Datagrid>
            <ReferenceField source="certificationTypeId" reference="certification-types">
              <TextField source="name" />
            </ReferenceField>
            <TextField source="otherName" />
            <DateField source="completedDate" />
            <DateField source="expiryDate" />
            <TextField source="status" />
          </Datagrid>
        </ReferenceManyField>
      </Tab>

      <Tab label="Education">
        <ReferenceManyField reference="driver-education" target="driverId">
          <Datagrid>
            <ReferenceField source="educationSchoolId" reference="education-schools">
              <TextField source="name" />
            </ReferenceField>
            <TextField source="programTitle" />
            <TextField source="schoolNameOther" />
            <DateField source="completedDate" />
            <TextField source="status" />
          </Datagrid>
        </ReferenceManyField>
      </Tab>

      <Tab label="Employment">
        <ReferenceManyField reference="driver-employment" target="driverId">
          <Datagrid>
            <TextField source="company" />
            <TextField source="title" />
            <DateField source="startDate" />
            <DateField source="endDate" />
            <BooleanField source="current" />
            <TextField source="city" />
            <TextField source="regionCode" />
          </Datagrid>
        </ReferenceManyField>
      </Tab>

      <Tab label="Vehicles">
        <ReferenceManyField reference="driver-vehicles" target="driverId">
          <Datagrid>
            <TextField source="jobVehicleMake" />
            <TextField source="jobVehicleModel" />
            <TextField source="jobVehicleYear" />
            <TextField source="lengthMonths" />
            <TextField source="mode" />
            <TextField source="status" />
          </Datagrid>
        </ReferenceManyField>
      </Tab>

      <Tab label="Files">
        <ReferenceManyField reference="driver-files" target="driverId">
          <Datagrid>
            <TextField source="fileType" />
            <ReferenceField source="fileId" reference="files">
              <TextField source="name" />
            </ReferenceField>
            <DateField source="createdAt" />
          </Datagrid>
        </ReferenceManyField>
      </Tab>
    </TabbedShowLayout>
  </Show>
);
